import React, { useState, useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Checkbox,
  IconButton,
  Tooltip,
  Chip,
  Typography,
  Toolbar,
  alpha,
  useTheme,
  Collapse,
  Menu,
  MenuItem,
  Button,
  Stack,
} from '@mui/material';
import {
  Edit,
  Delete,
  Visibility,
  Download,
  Share,
  MoreVert,
  FilterList,
  DeleteOutline,
  FileDownload,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { motion, AnimatePresence } from 'framer-motion';
import { MedicalDataFile } from '../../types/medicalDataList';
import GlassCard from '../common/GlassCard';

interface EnhancedDataTableProps {
  files: MedicalDataFile[];
  onEdit: (file: MedicalDataFile) => void;
  onDelete: (file: MedicalDataFile) => void;
  onView: (file: MedicalDataFile) => void;
  onDeleteMultiple?: (ids: string[]) => void;
  loading?: boolean;
}

interface HeadCell {
  id: keyof MedicalDataFile;
  label: string;
  numeric?: boolean;
  sortable?: boolean;
  width?: string;
}

const headCells: HeadCell[] = [
  { id: 'fileName', label: 'ファイル名', sortable: true },
  { id: 'uploadedAt', label: 'アップロード日時', sortable: true, width: '180px' },
  { id: 'uploadedBy', label: 'アップロード者', width: '150px' },
  { id: 'recordCount', label: 'レコード数', numeric: true, sortable: true, width: '120px' },
  { id: 'fileSize', label: 'ファイルサイズ', numeric: true, sortable: true, width: '140px' },
  { id: 'status', label: 'ステータス', width: '120px' },
];

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const getStatusChip = (status: MedicalDataFile['status']) => {
  const config = {
    uploaded: { label: 'アップロード済', color: 'default' as const },
    processing: { label: '処理中', color: 'warning' as const },
    completed: { label: '完了', color: 'success' as const },
    error: { label: 'エラー', color: 'error' as const },
  };

  const { label, color } = config[status];
  return <Chip size="small" label={label} color={color} sx={{ fontWeight: 600 }} />;
};

const EnhancedDataTable: React.FC<EnhancedDataTableProps> = ({
  files,
  onEdit,
  onDelete,
  onView,
  onDeleteMultiple,
  loading,
}) => {
  const theme = useTheme();
  const [selected, setSelected] = useState<string[]>([]);
  const [orderBy, setOrderBy] = useState<keyof MedicalDataFile>('uploadedAt');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState<{ [key: string]: HTMLElement | null }>({});

  const handleRequestSort = (property: keyof MedicalDataFile) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelecteds = files.map((file) => file.id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (id: string) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, fileId: string) => {
    setAnchorEl({ ...anchorEl, [fileId]: event.currentTarget });
  };

  const handleMenuClose = (fileId: string) => {
    setAnchorEl({ ...anchorEl, [fileId]: null });
  };

  const sortedFiles = useMemo(() => {
    const comparator = (a: MedicalDataFile, b: MedicalDataFile) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];

      if (aValue === undefined || bValue === undefined) {
        return 0;
      }

      if (bValue < aValue) {
        return order === 'desc' ? -1 : 1;
      }
      if (bValue > aValue) {
        return order === 'desc' ? 1 : -1;
      }
      return 0;
    };

    return [...files].sort(comparator);
  }, [files, order, orderBy]);

  const isSelected = (id: string) => selected.indexOf(id) !== -1;

  return (
    <GlassCard
      elevation={0}
      sx={{
        background: alpha(theme.palette.background.paper, 0.9),
        backdropFilter: 'blur(20px)',
      }}
    >
      {selected.length > 0 && (
        <Toolbar
          sx={{
            pl: { sm: 2 },
            pr: { xs: 1, sm: 1 },
            bgcolor: alpha(theme.palette.primary.main, 0.08),
          }}
        >
          <Typography sx={{ flex: '1 1 100%' }} color="inherit" variant="subtitle1" component="div">
            {selected.length} 件選択中
          </Typography>
          <Stack direction="row" spacing={1}>
            <Tooltip title="ダウンロード">
              <IconButton>
                <FileDownload />
              </IconButton>
            </Tooltip>
            <Tooltip title="削除">
              <IconButton
                onClick={() => onDeleteMultiple && onDeleteMultiple(selected)}
                sx={{ color: theme.palette.error.main }}
              >
                <DeleteOutline />
              </IconButton>
            </Tooltip>
          </Stack>
        </Toolbar>
      )}

      <TableContainer sx={{ maxHeight: 600 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  color="primary"
                  indeterminate={selected.length > 0 && selected.length < files.length}
                  checked={files.length > 0 && selected.length === files.length}
                  onChange={handleSelectAllClick}
                />
              </TableCell>
              {headCells.map((headCell) => (
                <TableCell
                  key={headCell.id}
                  align={headCell.numeric ? 'right' : 'left'}
                  padding="normal"
                  sortDirection={orderBy === headCell.id ? order : false}
                  sx={{
                    width: headCell.width,
                    backgroundColor: theme.palette.background.paper,
                    fontWeight: 600,
                  }}
                >
                  {headCell.sortable ? (
                    <TableSortLabel
                      active={orderBy === headCell.id}
                      direction={orderBy === headCell.id ? order : 'asc'}
                      onClick={() => handleRequestSort(headCell.id)}
                    >
                      {headCell.label}
                    </TableSortLabel>
                  ) : (
                    headCell.label
                  )}
                </TableCell>
              ))}
              <TableCell align="center" sx={{ width: '150px', backgroundColor: theme.palette.background.paper }}>
                アクション
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <AnimatePresence>
              {sortedFiles
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((file, index) => {
                  const isItemSelected = isSelected(file.id);
                  const labelId = `enhanced-table-checkbox-${index}`;

                  return (
                    <motion.tr
                      key={file.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      style={{ display: 'table-row' }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          color="primary"
                          checked={isItemSelected}
                          onChange={() => handleClick(file.id)}
                          inputProps={{ 'aria-labelledby': labelId }}
                        />
                      </TableCell>
                      <TableCell component="th" id={labelId} scope="row">
                        <Typography variant="body2" fontWeight={500}>
                          {file.fileName}
                        </Typography>
                        {file.tags && file.tags.length > 0 && (
                          <Box sx={{ mt: 0.5 }}>
                            {file.tags.map((tag) => (
                              <Chip
                                key={tag}
                                label={tag}
                                size="small"
                                sx={{ mr: 0.5, height: 20, fontSize: '0.75rem' }}
                              />
                            ))}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {format(new Date(file.uploadedAt), 'yyyy/MM/dd HH:mm', { locale: ja })}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{file.uploadedBy || '-'}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">{file.recordCount.toLocaleString()}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">{formatFileSize(file.fileSize)}</Typography>
                      </TableCell>
                      <TableCell>{getStatusChip(file.status)}</TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                          <Tooltip title="確認">
                            <IconButton
                              size="small"
                              onClick={() => onView(file)}
                              sx={{
                                color: theme.palette.info.main,
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.info.main, 0.08),
                                },
                              }}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="変更">
                            <IconButton
                              size="small"
                              onClick={() => onEdit(file)}
                              sx={{
                                color: theme.palette.warning.main,
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.warning.main, 0.08),
                                },
                              }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="削除">
                            <IconButton
                              size="small"
                              onClick={() => onDelete(file)}
                              sx={{
                                color: theme.palette.error.main,
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.error.main, 0.08),
                                },
                              }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, file.id)}
                          >
                            <MoreVert fontSize="small" />
                          </IconButton>
                          <Menu
                            anchorEl={anchorEl[file.id]}
                            open={Boolean(anchorEl[file.id])}
                            onClose={() => handleMenuClose(file.id)}
                            PaperProps={{
                              elevation: 1,
                              sx: {
                                minWidth: 140,
                                '& .MuiMenuItem-root': {
                                  fontSize: '0.875rem',
                                },
                              },
                            }}
                          >
                            <MenuItem onClick={() => handleMenuClose(file.id)}>
                              <Download fontSize="small" sx={{ mr: 1 }} /> ダウンロード
                            </MenuItem>
                            <MenuItem onClick={() => handleMenuClose(file.id)}>
                              <Share fontSize="small" sx={{ mr: 1 }} /> 共有
                            </MenuItem>
                          </Menu>
                        </Box>
                      </TableCell>
                    </motion.tr>
                  );
                })}
            </AnimatePresence>
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={files.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="表示件数:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}件`}
      />
    </GlassCard>
  );
};

export default EnhancedDataTable;